/*
 * SEO KEYWORD MAP — ADICIONAL NOTURNO HORÁRIO
 * URL: /adicional-noturno-horario
 * Primary: adicional noturno a partir de que horas (2.9K vol, KD 23)
 * Secondary: quanto é o adicional noturno (5.4K, KD 24)
 * LSI: hora noturna, horário noturno CLT, 22h às 5h, hora noturna reduzida,
 *       período noturno, turno da noite
 * Total: ~8,300/mo
 * Page type: Informational Guide
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import JsonLd from '../components/JsonLd';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import AdSlot from '../components/AdSlot';
import FAQAccordion from '../components/FAQAccordion';
import RelatedTools from '../components/RelatedTools';

const AdicionalNoturnoHorarioPage = () => {
  const faqs = [
    { question: 'A partir de que horas começa o adicional noturno?', answer: 'Para trabalhadores urbanos, o adicional noturno começa às 22h e vai até as 5h do dia seguinte (Art. 73, CLT). Para rurais na lavoura, das 21h às 5h. Na pecuária, das 20h às 4h.' },
    { question: 'Quanto é o adicional noturno?', answer: 'O adicional noturno é de no mínimo 20% sobre o valor da hora diurna para trabalhadores urbanos (Art. 73, CLT). Para trabalhadores rurais, o mínimo é 25% (Lei 5.889/73). Convenções coletivas podem estabelecer percentuais maiores.' },
    { question: 'O que é hora noturna reduzida?', answer: 'A hora noturna urbana equivale a 52 minutos e 30 segundos (não 60 minutos). Na prática, quem trabalha 7 horas noturnas (22h às 5h) recebe como se tivesse trabalhado 8 horas. Essa regra é exclusiva para trabalhadores urbanos — rurais não têm hora reduzida.' },
    { question: 'Quem trabalha até às 22h recebe adicional noturno?', answer: 'Não. O período noturno urbano começa às 22h. Se você encerra o turno exatamente às 22h, não trabalhou no horário noturno e não tem direito ao adicional.' },
    { question: 'E se eu trabalhar além das 5h da manhã?', answer: 'Se o trabalhador cumpre toda a jornada no período noturno (22h às 5h) e continua trabalhando depois das 5h, a Súmula 60, II, do TST garante o adicional noturno também sobre as horas prorrogadas.' },
    { question: 'Adicional noturno é obrigatório?', answer: 'Sim. O adicional noturno é um direito constitucional (Art. 7º, IX da Constituição Federal). Não pode ser suprimido por acordo individual ou convenção coletiva. É obrigatório para todo trabalhador CLT que atue no período noturno.' }
  ];

  const structuredData = { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": faqs.map(faq => ({ "@type": "Question", "name": faq.question, "acceptedAnswer": { "@type": "Answer", "text": faq.answer } })) };

  return (
    <>
      <Helmet>
        <html lang="pt-BR" />
        <title>Adicional Noturno: A Partir de Que Horas? Regras CLT</title>
        <meta name="description" content="Saiba a partir de que horas começa o adicional noturno (22h às 5h), quanto é o percentual (20%), hora noturna reduzida e regras da CLT. Guia completo." />
        <link rel="canonical" href="https://calculadoradehoras.net.br/adicional-noturno-horario" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Adicional Noturno: A Partir de Que Horas? Regras CLT" />
        <meta property="og:description" content="Horário noturno: 22h às 5h (urbano), 21h às 5h (rural lavoura), 20h às 4h (pecuária). Adicional mínimo de 20%." />
        <meta property="og:url" content="https://calculadoradehoras.net.br/adicional-noturno-horario" />
        <meta property="og:image" content="https://calculadoradehoras.net.br/og-image.png" />
        <meta property="og:site_name" content="Calculadora de Horas" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Adicional Noturno: A Partir de Que Horas? Regras CLT" />
        <meta name="twitter:description" content="Horário noturno: 22h às 5h (urbano), 21h às 5h (rural lavoura), 20h às 4h (pecuária). Adicional mínimo de 20%." />
        <meta property="og:type" content="article" />
        <meta property="og:locale" content="pt_BR" />
      </Helmet>
      <JsonLd data={structuredData} />
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
          {"@type":"ListItem","position":1,"name":"Início","item":"https://calculadoradehoras.net.br/"},
          {"@type":"ListItem","position":2,"name":"Adicional Noturno","item":"https://calculadoradehoras.net.br/calculadora-de-adicional-noturno"},
          {"@type":"ListItem","position":3,"name":"Horário do Adicional Noturno","item":"https://calculadoradehoras.net.br/adicional-noturno-horario"}
        ]
      }} />

      <Navigation />

      <main className="max-w-4xl mx-auto px-4 py-8">
        <nav className="text-sm text-body-text mb-4">
          <a href="/" className="hover:text-blue-primary">Início</a><span className="mx-2">›</span><a href="/calculadora-de-adicional-noturno" className="hover:text-blue-primary">Adicional Noturno</a><span className="mx-2">›</span><span>Horário</span>
        </nav>

        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-dark-text mb-3">Adicional Noturno: A Partir de Que Horas?</h1>
          <p className="text-lg text-body-text max-w-2xl mx-auto">Entenda o horário do adicional noturno, quanto é o percentual, como funciona a hora noturna reduzida e os direitos de quem trabalha à noite.</p>
          <span className="inline-block mt-2 text-sm text-body-text bg-light-bg px-3 py-1 rounded-full">Art. 73 da CLT — Atualizado 2026</span>
        </div>

        <div className="bg-white border-l-4 border-blue-primary rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-lg font-heading font-bold text-blue-primary mb-2">Horário do Adicional Noturno</h2>
          <p className="text-2xl font-heading font-extrabold text-dark-text">Das 22h às 5h (trabalhadores urbanos)</p>
          <p className="text-body-text mt-2">Acréscimo mínimo de <strong>20%</strong> sobre a hora diurna, com <strong>hora noturna reduzida</strong> de 52min30s.</p>
        </div>

        <AdSlot slotId="ad-slot-1" />

        <article className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">Horário Noturno por Categoria de Trabalhador</h2>
            <p className="text-body-text leading-relaxed mb-4">A CLT e a legislação rural definem horários diferentes para o período noturno, dependendo da categoria do trabalhador. Veja cada uma delas em detalhe.</p>
            <div className="space-y-4">
              <div className="bg-white border-2 border-border-gray rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <span className="text-3xl">🏙️</span>
                  <div>
                    <h3 className="font-semibold text-dark-text text-lg mb-1">Trabalhador Urbano</h3>
                    <p className="text-body-text mb-1"><strong>Horário:</strong> 22h00 às 05h00 do dia seguinte</p>
                    <p className="text-body-text mb-1"><strong>Adicional:</strong> Mínimo 20%</p>
                    <p className="text-body-text mb-1"><strong>Hora reduzida:</strong> Sim — 52 minutos e 30 segundos</p>
                    <p className="text-sm text-body-text"><strong>Base legal:</strong> Art. 73 da CLT</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border-2 border-border-gray rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <span className="text-3xl">🌾</span>
                  <div>
                    <h3 className="font-semibold text-dark-text text-lg mb-1">Trabalhador Rural — Lavoura</h3>
                    <p className="text-body-text mb-1"><strong>Horário:</strong> 21h00 às 05h00 do dia seguinte</p>
                    <p className="text-body-text mb-1"><strong>Adicional:</strong> Mínimo 25%</p>
                    <p className="text-body-text mb-1"><strong>Hora reduzida:</strong> Não se aplica</p>
                    <p className="text-sm text-body-text"><strong>Base legal:</strong> Lei 5.889/73, Art. 7º</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border-2 border-border-gray rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <span className="text-3xl">🐄</span>
                  <div>
                    <h3 className="font-semibold text-dark-text text-lg mb-1">Trabalhador Rural — Pecuária</h3>
                    <p className="text-body-text mb-1"><strong>Horário:</strong> 20h00 às 04h00 do dia seguinte</p>
                    <p className="text-body-text mb-1"><strong>Adicional:</strong> Mínimo 25%</p>
                    <p className="text-body-text mb-1"><strong>Hora reduzida:</strong> Não se aplica</p>
                    <p className="text-sm text-body-text"><strong>Base legal:</strong> Lei 5.889/73, Art. 7º</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">Quanto é o Adicional Noturno?</h2>
            <p className="text-body-text leading-relaxed mb-4">O <strong>valor do adicional noturno</strong> corresponde a um acréscimo percentual sobre o valor da hora diurna normal. O mínimo legal é de 20% para urbanos e 25% para rurais, mas muitas convenções coletivas definem percentuais maiores — como 30%, 35% ou até 50% em algumas categorias.</p>
            <p className="text-body-text leading-relaxed mb-4">Para saber exatamente quanto você deve receber de adicional noturno, use nossa <a href="/adicional-noturno-valor" className="text-blue-primary hover:underline">calculadora de valor do adicional noturno</a> ou a <a href="/calculadora-de-adicional-noturno" className="text-blue-primary hover:underline">calculadora completa de adicional noturno</a>.</p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">Hora Noturna Reduzida — Como Funciona</h2>
            <p className="text-body-text leading-relaxed mb-4">Uma das particularidades mais importantes do trabalho noturno é a <strong>hora noturna reduzida</strong>. Para trabalhadores urbanos, cada hora noturna equivale a apenas 52 minutos e 30 segundos (em vez de 60 minutos). Isso está previsto no Art. 73, § 1º da CLT.</p>
            <p className="text-body-text leading-relaxed mb-4">Na prática, isso significa que quem trabalha das 22h às 5h (7 horas no relógio) está completando uma jornada de 8 horas para fins de remuneração. Cada "hora" trabalhada vale mais.</p>
            <div className="bg-light-bg p-6 rounded-lg mb-4">
              <h3 className="text-xl font-heading font-semibold text-dark-text mb-2">Cálculo da Hora Noturna Reduzida</h3>
              <p className="text-body-text">7 horas noturnas de relógio ÷ 52,5 min × 60 min = <strong>8 horas trabalhadas</strong></p>
              <p className="text-body-text mt-2">Ou seja: 7h × (60 / 52,5) = 7h × 1,1429 = <strong>8 horas</strong></p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">Prorrogação do Horário Noturno</h2>
            <p className="text-body-text leading-relaxed mb-4">Uma dúvida frequente é: se o trabalhador começa o turno à noite e continua trabalhando após as 5h da manhã, ele perde o adicional noturno a partir das 5h?</p>
            <p className="text-body-text leading-relaxed mb-4">A resposta é <strong>não</strong>. A Súmula 60, II do TST determina que o adicional noturno se estende às horas trabalhadas em prorrogação ao período noturno. Ou seja, se você entrou às 22h e saiu às 7h, todas as horas de 22h às 7h têm direito ao adicional — não apenas até as 5h.</p>
            <p className="text-body-text leading-relaxed">Para calcular horas extras durante o período noturno, veja nossa <a href="/calculo-de-horas-extraordinarias" className="text-blue-primary hover:underline">calculadora de horas extraordinárias</a>.</p>
          </section>
        </article>

        <AdSlot slotId="ad-slot-2" />

        <section className="my-12">
          <h2 className="text-2xl font-heading font-bold text-dark-text mb-6">Perguntas Frequentes</h2>
          <FAQAccordion faqs={faqs} />
        </section>

        <RelatedTools currentPath="/adicional-noturno-horario" />
        <AdSlot slotId="ad-slot-3" />
      </main>

      <Footer />
    </>
  );
};

export default AdicionalNoturnoHorarioPage;
